import _styled from "@emotion/styled-base";
import React__default, { useState, useMemo, useCallback, useEffect, createElement } from 'react';
import { Checkbox } from '@risk-first/ui-checkbox';
import { Flex, Box, RegularText, Input, Button } from '@risk-first/ui-core';
import { rem } from 'polished';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';
import { themeGet } from '@styled-system/theme-get';
import { withTheme } from 'emotion-theming';
import { variant, layout } from 'styled-system';

const alphabeticalSort = (a, b) => {
  if (a === b) {
    return 0;
  }

  if (a < b) {
    return -1;
  }

  return 1;
};

const Wrapper = withTheme( /*#__PURE__*/_styled(Flex, {
  target: "e1ifddc50",
  label: "Wrapper"
})("padding-top:", themeGet('space.1'), "px;padding-bottom:", themeGet('space.1'), "px;min-width:200px;", variant({
  scale: 'textFilter'
}), ";"));
const CheckboxContainer = withTheme( /*#__PURE__*/_styled(Box, {
  target: "e1ifddc51",
  label: "CheckboxContainer"
})("padding:", themeGet('space.2'), "px 0;"));
const SelectAllCheckboxContainer = withTheme( /*#__PURE__*/_styled(CheckboxContainer, {
  target: "e1ifddc52",
  label: "SelectAllCheckboxContainer"
})("border-bottom:1px solid ", themeGet('filters.separatorColor'), ";"));
const Footer = withTheme( /*#__PURE__*/_styled(CheckboxContainer, {
  target: "e1ifddc53",
  label: "Footer"
})("border-top:1px solid ", themeGet('filters.separatorColor'), ";display:flex;justify-content:flex-end;"));
const Label = withTheme( /*#__PURE__*/_styled(RegularText, {
  target: "e1ifddc54",
  label: "Label"
})("font-size:", themeGet('fontSizes.0'), ";min-width:0;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;"));
const SearchTextInput = withTheme( /*#__PURE__*/_styled(Input, {
  target: "e1ifddc55",
  label: "SearchTextInput"
})("margin:", themeGet('space.0'), "px 0;", layout));
const OptionsContainer = withTheme( /*#__PURE__*/_styled(Box, {
  target: "e1ifddc56",
  label: "OptionsContainer"
})("height:", props => props.setHeight ? '265px' : 'auto', ";max-height:", props => props.setHeight ? 'auto' : '265px', ";overflow:auto;", variant({
  scale: 'options'
})));
const SEARCH_INPUT_APPEARANCE_THRESHOLD = 10;
/**
 * If the component is dealing with more than the amount of options
 * specified in this constant - the options will be virtualized
 */

const VIRTUALIZATION_ITEM_COUNT_THRESHOLD = 20;

const ControlledTextFilter = props => {
  const {
    onCheckToggle,
    onApply,
    id,
    sortFunction = alphabeticalSort
  } = props;
  const variant = props.variant || 'default';
  const [filterInput, setFilterInput] = useState('');
  const randomId = useMemo(() => id || String(Math.random()), [id]);
  const filters = useMemo(() => new Map(props.filters), [props.filters]);
  const showApplyButton = !!onApply;
  const visibleFilters = useMemo(() => filterInput ? new Map([...filters].filter(([k, v]) => k.toLowerCase().includes(filterInput.toLowerCase()))) : filters, [filterInput, filters]);
  const allSelected = [...filters.values()].every(f => f === true) || visibleFilters.size === 0;
  const shouldVirtualize = filters.size > VIRTUALIZATION_ITEM_COUNT_THRESHOLD;

  const toggleFilter = key => {
    const currentKeyValue = filters.get(key);
    filters.set(key, !currentKeyValue);

    if (onCheckToggle) {
      onCheckToggle(filters);
    }
  };

  const handleApply = () => {
    if (onApply) {
      onApply();
    }
  };

  const toggleAll = useCallback(() => {
    [...filters.keys()].forEach(key => {
      filters.set(key, !allSelected);
    });

    if (onCheckToggle) {
      onCheckToggle(filters);
    }
  }, [filters, onCheckToggle, allSelected]);
  const handleInputChange = useCallback(event => setFilterInput(event.target.value), []);
  const asArray = React__default.useMemo(() => [...visibleFilters.entries()].sort(([aName], [bName]) => sortFunction(aName, bName)), [visibleFilters, sortFunction]);

  const options = ({
    index,
    style
  }) => {
    const [name, value] = asArray[index];
    return React__default.createElement(CheckboxContainer, {
      key: name,
      style: style
    }, React__default.createElement(Checkbox, {
      id: `${name}-${randomId}`,
      value: name,
      checked: value,
      onChange: () => toggleFilter(name),
      title: name,
      stretch: true
    }, React__default.createElement(Label, null, name)));
  };

  return React__default.createElement(Wrapper, {
    "data-testid": "text-filters",
    flexDirection: 'column',
    variant: variant
  }, filters.size >= SEARCH_INPUT_APPEARANCE_THRESHOLD && React__default.createElement(Box, {
    pt: rem(12)
  }, React__default.createElement(SearchTextInput, {
    placeholder: "Search...",
    value: filterInput,
    onChange: handleInputChange,
    variant: "compact",
    width: 1
  })), React__default.createElement(SelectAllCheckboxContainer, null, React__default.createElement(Checkbox, {
    id: `select-all-${randomId}`,
    value: 'select-all',
    checked: allSelected,
    onChange: toggleAll,
    stretch: true
  }, React__default.createElement(Label, null, "(Select All)"))), React__default.createElement(OptionsContainer, {
    setHeight: shouldVirtualize,
    variant: variant
  }, shouldVirtualize ? React__default.createElement(AutoSizer, null, ({
    height,
    width
  }) => {
    return React__default.createElement(FixedSizeList, {
      height: height,
      width: width,
      itemCount: visibleFilters.size,
      itemSize: 37
    }, options);
  }) : asArray.map((_, index) => ({
    index,
    style: {}
  })).map(options)), showApplyButton && React__default.createElement(Footer, null, React__default.createElement(Button, {
    variant: "linkTransparent",
    size: "small",
    onClick: handleApply
  }, "Apply")));
};

function updateMap(filters) {
  return map => {
    return new Map(Array.from(filters).map(([key]) => {
      if (map.has(key)) {
        return [key, map.get(key)];
      }

      return [key, true];
    }));
  };
}

const FormTextFilter = props => {
  const {
    onChange,
    sortFunction,
    filters,
    id
  } = props;
  const [filtersMap, setFiltersMap] = useState(filters);
  useEffect(() => {
    setFiltersMap(updateMap(filters));
  }, [filters]);
  const handleCheckToggle = useCallback(map => {
    setFiltersMap(map);
  }, []);
  const handleApply = useCallback(() => {
    onChange(filtersMap);
  }, [filtersMap, onChange]);
  return createElement(ControlledTextFilter, {
    id: id,
    filters: filtersMap,
    sortFunction: sortFunction,
    onCheckToggle: handleCheckToggle,
    onApply: handleApply
  });
};

const getSelectedFiltersArray = filters => Array.from(filters, ([filterText, selected]) => {
  if (selected) {
    return filterText;
  }

  return false;
}).filter(Boolean);

export { ControlledTextFilter, FormTextFilter, getSelectedFiltersArray };
//# sourceMappingURL=index.js.map
